import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Business,
  Settings,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const UserMenu: React.FC = () => {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!user) return null;

  return (
    <Box>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ ml: 2 }}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
          {getInitials(user.full_name)}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 250,
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {user.full_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'inline-block',
              mt: 0.5,
              px: 1,
              py: 0.25,
              bgcolor: user.role === 'superuser' ? 'error.light' : user.role === 'admin' ? 'warning.light' : 'info.light',
              color: 'white',
              borderRadius: 1,
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              fontWeight: 600,
            }}
          >
            {user.role}
          </Typography>
        </Box>

        <Divider />

        {/* Organization Info */}
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <ListItemIcon>
            <Business fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={organization?.name}
            secondary={`Invite: ${organization?.invite_code}`}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </MenuItem>

        <Divider />

        {/* Profile Settings */}
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile Settings</ListItemText>
        </MenuItem>

        {/* Organization Settings (All users) */}
        <MenuItem onClick={() => navigate('/organization')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText>Organization Settings</ListItemText>
        </MenuItem>

        {/* Superuser Menu Items */}
        {user.role === 'superuser' && (
          <>
            <MenuItem onClick={() => navigate('/superuser/create-organization')}>
              <ListItemIcon>
                <Business fontSize="small" />
              </ListItemIcon>
              <ListItemText>Create Organization</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => navigate('/superuser/manage-organizations')}>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage Organizations</ListItemText>
            </MenuItem>
          </>
        )}

        <Divider />

        {/* Logout */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
